import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Loader2, ArrowRight, Phone, Mail, MessageCircle, Check } from "lucide-react";
import { EMAILJS_CONFIG, BRAND } from "@/lib/constants";
import { useReveal } from "@/hooks/useReveal";

export default function Contacto() {
    const { ref, visible } = useReveal();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        whatsapp: "",
        message: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (
            !form.name.trim() ||
            !form.email.trim() ||
            !form.whatsapp.trim() ||
            !form.message.trim()
        ) {
            toast.error("Por favor completa todos los campos");
            return;
        }
        setSubmitting(true);
        try {
            await emailjs.send(
                EMAILJS_CONFIG.serviceId,
                EMAILJS_CONFIG.templateId,
                {
                    form_type: "Contacto — Diagnóstico de imagen",
                    name: form.name,
                    from_name: form.name,
                    email: form.email,
                    reply_to: form.email,
                    whatsapp: form.whatsapp,
                    message: form.message,
                    to_email: EMAILJS_CONFIG.toEmail,
                },
                { publicKey: EMAILJS_CONFIG.publicKey }
            );
            setSubmitted(true);
            toast.success("Mensaje enviado. Te contactaré muy pronto.");
            setForm({ name: "", email: "", whatsapp: "", message: "" });
        } catch (err) {
            console.error("EmailJS error:", err);
            toast.error("No pudimos enviar tu mensaje. Inténtalo de nuevo.");
        } finally {
            setSubmitting(false);
        }
    };

    const waLink = `https://wa.me/${BRAND.whatsappRaw}?text=${encodeURIComponent(
        "Hola Ariadna, me interesa agendar mi diagnóstico de imagen."
    )}`;

    return (
        <section
            id="contacto"
            data-testid="contacto-section"
            className="relative py-28 md:py-40 bg-[#1E1E1E] text-[#F7F3EE] overflow-hidden grain"
        >
            <div className="blur-shape w-[480px] h-[480px] bg-[#C6A76A]/15 -top-32 -right-40" />
            <div className="blur-shape w-[360px] h-[360px] bg-[#D8B9A7]/10 -bottom-32 -left-32" />

            <div
                ref={ref}
                className={`relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 reveal ${visible ? "is-visible" : ""}`}
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left: CTA copy */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="text-[12px] tracking-[0.32em] uppercase text-[#C6A76A]">
                                Contacto
                            </span>
                            <span className="h-px w-12 bg-[#C6A76A]" />
                        </div>

                        <h2
                            data-testid="contacto-title"
                            className="font-display text-[44px] md:text-[68px] leading-[1.02] tracking-[-0.02em]"
                        >
                            Si tu imagen no está alineada con lo que{" "}
                            <span className="font-italic-display text-[#C6A76A]">eres hoy</span>, puedo ayudarte.
                        </h2>

                        <p
                            data-testid="contacto-subtext"
                            className="mt-8 text-base md:text-lg text-[#F7F3EE]/75 font-light max-w-md leading-relaxed"
                        >
                            👉 Escríbeme por WhatsApp y agenda tu diagnóstico.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contacto-whatsapp-link"
                                className="group flex items-center gap-5 py-5 border-b border-[#F7F3EE]/15 hover:border-[#C6A76A] transition-colors"
                            >
                                <Phone size={20} strokeWidth={1.25} className="text-[#C6A76A]" />
                                <div className="flex-1">
                                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/50">
                                        WhatsApp
                                    </div>
                                    <div className="font-display text-xl md:text-2xl text-[#F7F3EE] mt-0.5">
                                        {BRAND.whatsappDisplay}
                                    </div>
                                </div>
                                <ArrowRight
                                    size={18}
                                    className="text-[#F7F3EE]/40 group-hover:text-[#C6A76A] group-hover:translate-x-1 transition-all"
                                />
                            </a>
                            <a
                                href={`mailto:${BRAND.email}`}
                                data-testid="contacto-email-link"
                                className="group flex items-center gap-5 py-5 border-b border-[#F7F3EE]/15 hover:border-[#C6A76A] transition-colors"
                            >
                                <Mail size={20} strokeWidth={1.25} className="text-[#C6A76A]" />
                                <div className="flex-1">
                                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/50">
                                        Correo
                                    </div>
                                    <div className="font-display text-lg md:text-xl text-[#F7F3EE] mt-0.5 break-all">
                                        {BRAND.email}
                                    </div>
                                </div>
                                <ArrowRight
                                    size={18}
                                    className="text-[#F7F3EE]/40 group-hover:text-[#C6A76A] group-hover:translate-x-1 transition-all"
                                />
                            </a>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7">
                        <div className="relative bg-[#F7F3EE]/[0.04] backdrop-blur-sm border border-[#F7F3EE]/12 p-8 md:p-12">
                            {submitted ? (
                                <div data-testid="contacto-success" className="py-16 text-center">
                                    <div className="mx-auto w-14 h-14 rounded-full bg-[#C6A76A] flex items-center justify-center mb-6">
                                        <Check size={24} className="text-[#1E1E1E]" strokeWidth={2.5} />
                                    </div>
                                    <h3 className="font-display text-3xl mb-3">
                                        Mensaje enviado
                                    </h3>
                                    <p className="text-[#F7F3EE]/75 font-light max-w-sm mx-auto">
                                        Gracias por escribirme. Me pondré en contacto contigo lo más pronto posible para tu diagnóstico.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setSubmitted(false)}
                                        data-testid="contacto-reset"
                                        className="mt-8 text-[11px] tracking-[0.24em] uppercase text-[#C6A76A] underline underline-offset-4"
                                    >
                                        Enviar otro mensaje
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} data-testid="contacto-form">
                                    <div className="flex items-center gap-2 mb-3 text-[#C6A76A]">
                                        <MessageCircle size={16} strokeWidth={1.25} />
                                        <span className="text-[11px] tracking-[0.28em] uppercase">
                                            Agenda tu diagnóstico
                                        </span>
                                    </div>
                                    <h3 className="font-display text-3xl md:text-4xl leading-tight mb-10">
                                        Hablemos de tu <span className="font-italic-display text-[#C6A76A]">imagen</span>.
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
                                        <div>
                                            <label htmlFor="c-name" className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/55">
                                                Nombre
                                            </label>
                                            <input
                                                id="c-name"
                                                data-testid="contacto-input-name"
                                                type="text"
                                                name="name"
                                                value={form.name}
                                                onChange={handleChange}
                                                placeholder="Tu nombre"
                                                className="editorial-input-dark"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="c-email" className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/55">
                                                Correo
                                            </label>
                                            <input
                                                id="c-email"
                                                data-testid="contacto-input-email"
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                placeholder="tu@email.com"
                                                className="editorial-input-dark"
                                                required
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label htmlFor="c-whatsapp" className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/55">
                                                WhatsApp
                                            </label>
                                            <input
                                                id="c-whatsapp"
                                                data-testid="contacto-input-whatsapp"
                                                type="tel"
                                                name="whatsapp"
                                                value={form.whatsapp}
                                                onChange={handleChange}
                                                placeholder="+52 ..."
                                                className="editorial-input-dark"
                                                required
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label htmlFor="c-message" className="text-[10px] tracking-[0.28em] uppercase text-[#F7F3EE]/55">
                                                Mensaje
                                            </label>
                                            <textarea
                                                id="c-message"
                                                data-testid="contacto-input-message"
                                                name="message"
                                                value={form.message}
                                                onChange={handleChange}
                                                placeholder="Cuéntame qué buscas — eventos, día a día, redes..."
                                                rows={4}
                                                className="editorial-input-dark resize-none"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        data-testid="contacto-submit"
                                        disabled={submitting}
                                        className="mt-12 inline-flex items-center gap-3 px-9 py-4 bg-[#C6A76A] text-[#1E1E1E] font-medium text-[13px] tracking-[0.18em] uppercase rounded-full hover:bg-[#F7F3EE] transition-all duration-400 disabled:opacity-60"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" /> Enviando...
                                            </>
                                        ) : (
                                            <>
                                                Enviar mensaje
                                                <ArrowRight size={16} />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
